import { ApiService } from 'projects/admin/src/app/services/api.service';
import { UiService } from 'projects/admin/src/app/services/ui.service';
import { TexttemplateModel } from 'projects/core/src/lib/models/texttemplate.model';
import { BaseForm } from 'projects/admin/src/app/classes/base-form';
import { Component, Input, Output, EventEmitter, OnInit, OnChanges } from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { LanguageModel } from 'projects/core/src/lib/models/language.model';
import { ResourceModel } from 'projects/core/src/lib/models/resource.model';
import { AuthStateService } from 'projects/admin/src/app/services/auth-state.service';
import { ConfirmationService } from 'primeng/api';
import { PillItem } from 'projects/admin/src/app/shared/pill-nav/pill-nav.component';

class AddResourceModel extends ResourceModel {
  public added: boolean = false;
}

@Component({
  selector: 'app-texttemplate-files',
  templateUrl: './texttemplate-files.component.html',
  styleUrls: ['./texttemplate-files.component.scss']
})
export class TexttemplateFilesComponent extends BaseForm implements OnInit, OnChanges {

  @Input() texttemplate: TexttemplateModel = null;
  @Input() languages: Array<LanguageModel> = [];
  @Output() setEdit = new EventEmitter<string>();

  public selectedLanguage: LanguageModel = null;
  public navEntries: Array<PillItem> = [];
  public fileLists: { [key: string]: Array<ResourceModel> } = {};
  public allFiles: Array<AddResourceModel> = [];
  public addFileVisible = false;
  public filesLoading = false;
  public submittingFiles = false;

  constructor(
    private ui: UiService,
    private api: ApiService,
    private auth: AuthStateService,
    private confirmationService: ConfirmationService,
    public transloco: TranslocoService
  ) {
    super();
  }

  ngOnInit() {
    this.buildNavItems();
    this.ui.setFocus();
  }

  ngOnChanges(): void {
    this.buildFileLists();
  }

  protected buildFileLists(): void {
    this.fileLists = {};
    this.texttemplate.files.forEach((file: ResourceModel) => {
      const language_id = file.pivot.language_id;
      if (language_id) {
        if (!this.fileLists[language_id]) {
          this.fileLists[language_id] = [];
        }
        this.fileLists[language_id].push(file);
      }
    });
    this.languages.forEach((language: LanguageModel) => {
      if (Array.isArray(this.fileLists[language.id])) {
        this.fileLists[language.id].sort((fileA: AddResourceModel, fileB: AddResourceModel) => {
          return (fileA.public_name > fileB.public_name) ? 1 : ((fileB.public_name > fileA.public_name) ? -1 : 0);
        });
      }
    });
  }

  protected buildNavItems(): void {
    if (this.languages.length > 0) {
      this.navEntries = this.languages.map((language: LanguageModel): PillItem => {
        return {
          id: language.id,
          label: language.key,
        };
      });
      this.selectedLanguage = this.languages[0];
    }
    this.setNavContentState();
  }

  protected setNavContentState(): void {
    this.navEntries.forEach((entry: PillItem) => {
      entry.hasContent = this.fileLists[entry.id] && this.fileLists[entry.id].length > 0;
    });
  }

  public onSelectLanguage(entry: PillItem): void {
    this.selectedLanguage = this.languages.find((language: LanguageModel) => {
      return language.id === entry.id;
    });
  }

  public onDeleteFile(file: ResourceModel): void {
    if (this.auth.hasRight('admin.texttemplate.edit')) {
      this.confirmationService.confirm({
        message: this.transloco.translate('common.messages.really_delete', { value: file.public_name }),
        header: this.transloco.translate('common.really_delete'),
        icon: 'pi pi-exclamation-triangle',
        accept: () => {
          this.api.deleteTexttemplateFile(this.texttemplate.id, this.selectedLanguage.id, file.id).subscribe({
            next: (result: any) => {
              this.ui.toastSuccess(this.transloco.translate('common.messages.deleted', { value: file.public_name }), this.transloco.translate('common.deleted'));
              this.texttemplate = new TexttemplateModel().deserialize(result.data);
              this.buildFileLists();
              this.setNavContentState();
            },
            error: (error: any) => {
              this.ui.toastError(this.transloco.translate(error.message));
            }
          });
        }
      });
    }
  }

  public onFileAdd(): void {
    this.allFiles = [];
    this.addFileVisible = true;
    this.filesLoading = true;
    this.api.getTexttemplateFiles(this.texttemplate.id).subscribe({
      next: (result: any) => {
        this.allFiles = result.data.map((file: any) => {
          return new AddResourceModel().deserialize(file);
        });
      },
      error: (error: any) => {}
    }).add(() => {
      this.filesLoading = false;
    });
    this.allFiles.sort((fileA: AddResourceModel, fileB: AddResourceModel) => {
      return (fileA.public_name > fileB.public_name) ? 1 : ((fileB.public_name > fileA.public_name) ? -1 : 0);
    });
  }

  public toggleFile(file: AddResourceModel) {
    file.added = !file.added;
  }

  public onAddSubmit(): void {
    if (this.auth.hasRight('admin.texttemplate.edit')) {
      this.submittingFiles = true;
      const filesIds = this.allFiles.filter((file: AddResourceModel) => {
        return file.added === true;
      }).map((file: AddResourceModel) => {
        return file.id;
      });
      this.api.saveTexttemplateFile(this.texttemplate.id, this.selectedLanguage.id, filesIds).subscribe({
        next: (result: any) => {
          this.ui.toastSuccess(this.transloco.translate('common.messages.files_added', { value: filesIds.length}), this.transloco.translate('common.added'));
          this.texttemplate = new TexttemplateModel().deserialize(result.data);
          this.buildFileLists();
          this.setNavContentState();
        },
        error: (error: any) => {
          this.ui.toastError(this.transloco.translate(error.message));
        }
      }).add(() => {
        this.addFileVisible = false;
        this.submittingFiles = false;
      });
    }
  }

}
